import { useRef } from "react";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowDown, Crosshair } from "lucide-react";

const HERO_IMG = "/assets/osh-logo.jpeg";

const lines = [
  { text: "PRECISION.", cls: "text-white" },
  { text: "POWER.", cls: "text-white" },
  { text: "PERFORMANCE.", cls: "text-gold italic" },
];

export default function Hero() {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "22%"]);
  const fade = useTransform(scrollYProgress, [0, 0.7], [1, 0]);

  return (
    <section id="top" ref={ref} className="relative isolate min-h-screen flex flex-col justify-end overflow-hidden" data-testid="hero-section">
      <motion.div style={{ y: bgY }} className="absolute inset-0 -z-10" aria-hidden="true">
        <img src={HERO_IMG} alt="" className="w-full h-[120%] object-contain object-center opacity-40" />
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/40 to-black/60" />
      </motion.div>

      <div className="max-w-[1600px] mx-auto w-full px-5 md:px-10 pt-36 pb-10 md:pb-16">
        <motion.div
          initial={{ opacity: 0, x: -24 }}
          animate={{ opacity: 1, x: 0 }}
          transition={{ duration: 0.7, delay: 0.5 }}
          className="flex items-center gap-3 mb-6"
        >
          <Crosshair className="w-4 h-4 text-gold" />
          <span className="font-display uppercase tracking-[0.35em] text-xs md:text-sm text-gold" data-testid="hero-eyebrow">
            Strength · Rehab · Performance · Northwood
          </span>
        </motion.div>

        <h1 className="font-display font-bold uppercase leading-[0.86] tracking-tight text-[17vw] md:text-[10.5rem] lg:text-[12rem]" data-testid="hero-headline">
          {lines.map((line, i) => (
            <span key={line.text} className="block overflow-hidden pb-1">
              <motion.span
                className={`block ${line.cls}`}
                initial={{ y: "110%" }}
                animate={{ y: 0 }}
                transition={{ duration: 1, ease: [0.22, 1, 0.36, 1], delay: 0.35 + i * 0.14 }}
              >
                {line.text}
              </motion.span>
            </span>
          ))}
        </h1>

        <motion.div
          style={{ opacity: fade }}
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8, delay: 1.1 }}
          className="mt-10 md:mt-14 flex flex-col md:flex-row md:items-end justify-between gap-8 border-t border-white/10 pt-8"
        >
          <p className="max-w-md text-zinc-400 text-base md:text-lg leading-relaxed" data-testid="hero-subcopy">
            I'm Harry — a personal trainer in Northwood built on intentional training. I help busy parents,
            hard workers, the older generation and athletes move better, get stronger and reach their goals.
          </p>
          <div className="flex items-center gap-4">
            <a
              href="#contact"
              data-testid="hero-cta-primary"
              className="inline-flex items-center gap-2 bg-gold text-black font-display font-semibold uppercase tracking-[0.15em] px-8 py-4 hover:bg-gold-bright transition-colors duration-300"
            >
              Start With A Consultation
            </a>
            <a
              href="#method"
              data-testid="hero-cta-secondary"
              className="hidden sm:inline-flex items-center gap-2 border border-white/20 text-white font-display uppercase tracking-[0.15em] px-8 py-4 hover:border-gold hover:text-gold transition-colors duration-300"
            >
              About The Gym
            </a>
          </div>
        </motion.div>
      </div>

      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ delay: 1.6 }}
        className="absolute bottom-6 right-6 md:right-10 hidden md:flex items-center gap-2 text-zinc-500"
        aria-hidden="true"
      >
        <ArrowDown className="w-4 h-4 animate-bounce" />
        <span className="font-display uppercase tracking-[0.3em] text-xs">Scroll</span>
      </motion.div>
    </section>
  );
}
